import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, ChevronRight } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Seo, organizationSchema } from "@/lib/seo";
import { Reveal, stagger, item } from "@/components/Reveal";
import { DynIcon } from "@/components/DynIcon";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SOLUTION_DETAILS, SOLUTION_ORDER } from "@/lib/mockData";

const SolutionDetail = () => {
  const { slug } = useParams();
  const data = SOLUTION_DETAILS[slug];

  if (!data) return <Navigate to="/services" replace />;

  const related = SOLUTION_ORDER.filter((s) => s !== slug).map((s) => SOLUTION_DETAILS[s]);

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: data.title,
    description: data.subtitle,
    provider: { "@type": "Organization", name: "Elikya Technology" },
    areaServed: ["South Africa", "Democratic Republic of the Congo"],
  };

  return (
    <Layout>
      <Seo
        title={data.title}
        description={data.subtitle}
        keywords={`${data.title}, ${data.title} africa, merchant payment gateway africa, enterprise payment infrastructure, mobile money integration hub, multi-currency settlement africa, elikya technology, fintech africa`}
        path={`/solutions/${slug}`}
        jsonLd={[organizationSchema, serviceSchema]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-stroke">
        <div className="pointer-events-none absolute inset-0 grid-dots opacity-60" />
        <div className="pointer-events-none absolute -top-20 right-[-8%] h-96 w-96 rounded-full bg-brand-tint blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-20">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-slatey">
            <Link to="/services" className="hover:text-brand">Services</Link>
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="font-medium text-ink">{data.title}</span>
          </nav>

          <Reveal className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-stroke bg-white px-3.5 py-1.5 text-xs font-semibold text-brand">
              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-brand text-white">
                <DynIcon name={data.icon} className="h-3 w-3" />
              </span>
              {data.eyebrow}
            </span>
            <h1 className="mt-5 text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl">
              {data.title}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slatey sm:text-lg">{data.subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/signup"
                data-testid="solution-cta"
                className="group inline-flex items-center gap-2 rounded-xl bg-brand px-5 py-3 text-[0.95rem] font-semibold text-white transition-all hover:bg-brand-hover"
              >
                Get Started <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-xl border border-stroke bg-white px-5 py-3 text-[0.95rem] font-semibold text-ink transition-all hover:bg-brand-tint"
              >
                Talk to our team
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OVERVIEW + OUTCOMES */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <Reveal>
            <h2 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl">Overview</h2>
            <p className="mt-4 text-base leading-relaxed text-slatey">{data.overview}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-3 gap-4 rounded-2xl border border-stroke bg-white p-6 shadow-sm">
              {data.outcomes.map((o) => (
                <div key={o.label} className="text-center">
                  <p className="text-2xl font-bold tracking-tight text-brand tabular-nums sm:text-3xl">{o.stat}</p>
                  <p className="mt-1 text-xs leading-snug text-slatey">{o.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="border-y border-stroke bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <h2 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl">What's included</h2>
          </Reveal>
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="show"
            className="mt-10 grid gap-5 sm:grid-cols-2"
          >
            {data.capabilities.map((c) => (
              <motion.article
                key={c.title}
                variants={item}
                data-testid={`capability-${c.title.split(" ")[0].toLowerCase()}`}
                className="group flex gap-4 rounded-2xl border border-stroke bg-canvas p-6 transition-all duration-300 hover:border-brand/40 hover:bg-white hover:shadow-float"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-tint text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                  <DynIcon name={c.icon} className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="text-base font-semibold text-ink">{c.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slatey">{c.desc}</p>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      {data.faqs?.length > 0 && (
        <section className="mx-auto max-w-3xl px-5 sm:px-8 py-16 lg:py-20">
          <Reveal>
            <h2 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl">Questions &amp; answers</h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-8">
            <Accordion type="single" collapsible className="space-y-3">
              {data.faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="rounded-2xl border border-stroke bg-white px-5 shadow-sm"
                >
                  <AccordionTrigger className="text-left text-base font-semibold text-ink hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-slatey">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </section>
      )}

      {/* RELATED SOLUTIONS */}
      <section className="border-t border-stroke bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="flex items-center justify-between">
            <h2 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl">Explore other solutions</h2>
            <Link to="/services" className="hidden items-center gap-1.5 text-sm font-semibold text-brand hover:text-brand-hover sm:inline-flex">
              <ArrowLeft className="h-4 w-4" /> All services
            </Link>
          </Reveal>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/solutions/${r.slug}`}
                data-testid={`related-${r.slug}`}
                className="group flex items-start gap-4 rounded-2xl border border-stroke bg-canvas p-6 transition-all duration-300 hover:-translate-y-1 hover:border-brand/40 hover:bg-white hover:shadow-float"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand text-white">
                  <DynIcon name={r.icon} className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="text-base font-semibold text-ink">{r.title}</h3>
                  <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-brand">
                    Learn more <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SolutionDetail;
